import * as Dialog from "@radix-ui/react-dialog";
import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";
import { fetchReportFile, type ReportCategory, type ReportEvent, type ReportFile, type ReportStatus } from "../../api/reports";
import { fileKind, fileSize } from "../../lib/format";
import { CheckIcon, ClockIcon, CloseIcon, DownloadIcon, SparkIcon } from "../../ui/icons";
import { IconButton } from "../../ui/IconButton";
import { Spinner } from "../../ui/Spinner";
import { toast } from "../../ui/toast";

export const CATEGORY_LABEL: Record<ReportCategory, string> = {
  bug: "Что-то не работает",
  answer: "Ответ ассистента",
  files: "Файлы и документы",
  access: "Вход и доступ",
  idea: "Предложение",
  other: "Другое",
};
export const CATEGORIES = Object.keys(CATEGORY_LABEL) as ReportCategory[];

export const STATUS_LABEL: Record<ReportStatus, string> = { new: "Новое", in_progress: "В работе", done: "Решено" };
export const STATUSES = Object.keys(STATUS_LABEL) as ReportStatus[];

/** Статус обращения: цвет дублируется словом и значком. */
export function StatusBadge({ status }: { status: ReportStatus }) {
  const cls = { new: "bg-warn-soft text-warn-ink", in_progress: "bg-accent-soft text-accent-ink", done: "bg-ok-soft text-ok" }[status];
  return (
    <span className={`inline-flex h-6 items-center gap-1 rounded-full px-2.5 text-xs font-semibold whitespace-nowrap [&_svg]:size-3.5 ${cls}`}>
      {status === "new" ? <SparkIcon /> : status === "in_progress" ? <ClockIcon /> : <CheckIcon />}
      {STATUS_LABEL[status]}
    </span>
  );
}

export function when(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const time = d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === today.toDateString()) return `сегодня, ${time}`;
  const y = new Date(today);
  y.setDate(y.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return `вчера, ${time}`;
  return `${d.toLocaleDateString("ru-RU", { day: "numeric", month: "long", ...(d.getFullYear() !== today.getFullYear() ? { year: "numeric" } : {}) })}, ${time}`;
}

const isImage = (f: ReportFile) => /^image\/(png|jpeg|gif|webp)/.test(f.mime);
const isVideo = (f: ReportFile) => /^video\/(mp4|webm|quicktime)/.test(f.mime);

/** Байты вложения как blob: URL; освобождается при размонтировании. */
function useBlobUrl(url: string | null) {
  const q = useQuery({
    queryKey: ["report-file", url],
    queryFn: ({ signal }) => fetchReportFile(url!, signal),
    enabled: !!url,
    staleTime: Infinity,
    gcTime: 60_000,
  });
  const [obj, setObj] = useState<string | null>(null);
  useEffect(() => {
    if (!q.data) return;
    const u = URL.createObjectURL(q.data);
    setObj(u);
    return () => URL.revokeObjectURL(u);
  }, [q.data]);
  return { url: obj, error: q.isError };
}

async function download(url: string, name: string) {
  try {
    const blob = await fetchReportFile(url);
    const href = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement("a"), { href, download: name });
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  } catch {
    toast("Не удалось скачать файл. Попробуйте ещё раз.");
  }
}

function Thumb({ file, src, onOpen }: { file: ReportFile; src: string; onOpen: () => void }) {
  const blob = useBlobUrl(src);
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Открыть ${file.name}`}
      className="pressable group relative aspect-[4/3] overflow-hidden rounded-control border border-line bg-subtle transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-card"
    >
      {blob.url ? (
        <img src={blob.url} alt="" className="size-full object-cover" />
      ) : (
        <span className="grid size-full place-items-center text-sub">{blob.error ? "нет файла" : <Spinner className="size-5" />}</span>
      )}
      <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/55 to-transparent px-2 pt-4 pb-1.5 text-left text-xs font-medium text-white">
        {file.name}
      </span>
    </button>
  );
}

/** Просмотр картинки или записи экрана во весь экран. */
function Viewer({ file, src, onClose }: { file: ReportFile | null; src: string | null; onClose: () => void }) {
  const blob = useBlobUrl(src);
  return (
    <Dialog.Root open={!!file} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] bg-black/80 data-[state=open]:animate-[fade_200ms_ease-out]" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed inset-0 z-[71] flex flex-col p-3 outline-none data-[state=open]:animate-[fade_200ms_ease-out] md:p-6"
        >
          <div className="flex items-center gap-2 text-white">
            <Dialog.Title className="min-w-0 flex-1 truncate text-sm font-semibold">{file?.name}</Dialog.Title>
            {file && src && (
              <IconButton label="Скачать" onClick={() => void download(src, file.name)} className="text-white hover:bg-white/15 hover:text-white">
                <DownloadIcon />
              </IconButton>
            )}
            <IconButton label="Закрыть" onClick={onClose} tip={false} className="text-white hover:bg-white/15 hover:text-white">
              <CloseIcon />
            </IconButton>
          </div>
          <div className="grid min-h-0 flex-1 place-items-center pt-3" onClick={onClose}>
            {!blob.url ? (
              <Spinner className="size-6 text-white" />
            ) : file && isVideo(file) ? (
              <video src={blob.url} controls autoPlay className="max-h-full max-w-full rounded-control" onClick={(e) => e.stopPropagation()} />
            ) : (
              <img src={blob.url} alt={file?.name ?? ""} className="max-h-full max-w-full rounded-control object-contain" onClick={(e) => e.stopPropagation()} />
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Вложения: картинки плиткой с просмотром, остальное — карточки со скачиванием. */
export function Attachments({ files, urlOf }: { files: ReportFile[]; urlOf: (fileId: string) => string }) {
  const [open, setOpen] = useState<ReportFile | null>(null);
  if (files.length === 0) return null;
  const images = files.filter(isImage);
  const rest = files.filter((f) => !isImage(f));
  return (
    <div className="space-y-2.5">
      {images.length > 0 && (
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
          {images.map((f) => (
            <Thumb key={f.id} file={f} src={urlOf(f.id)} onOpen={() => setOpen(f)} />
          ))}
        </div>
      )}
      {rest.length > 0 && (
        <ul className="space-y-2">
          {rest.map((f) => (
            <li key={f.id} className="flex min-h-14 items-center gap-2.5 rounded-control border border-line bg-surface py-2 pr-1.5 pl-2">
              <span aria-hidden className="inline-grid size-[34px] flex-none place-items-center rounded-[9px] bg-subtle text-[11px] font-bold text-ink-2">
                {fileKind(f.name)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{f.name}</span>
                <span className="block text-xs text-sub">{fileSize(f.size)}</span>
              </span>
              {isVideo(f) && (
                <button type="button" onClick={() => setOpen(f)} className="pressable h-10 rounded-control px-3 text-sm font-semibold text-accent-ink hover:bg-hover pointer-coarse:h-11">
                  Смотреть
                </button>
              )}
              <IconButton label={`Скачать ${f.name}`} size="sm" onClick={() => void download(urlOf(f.id), f.name)}>
                <DownloadIcon />
              </IconButton>
            </li>
          ))}
        </ul>
      )}
      <Viewer file={open} src={open ? urlOf(open.id) : null} onClose={() => setOpen(null)} />
    </div>
  );
}

/**
 * История обращения: «отправлено», смены статуса и ответы. Ответы — карточками, смены статуса — строкой.
 * `supportName` — как подписать ответ без автора (пользователю имя администратора не показываем).
 */
export function Timeline({ createdAt, events, supportName = "Поддержка" }: { createdAt: string; events: ReportEvent[]; supportName?: string }) {
  const reduce = useReducedMotion();
  return (
    <ol className="relative space-y-3 before:absolute before:top-2 before:bottom-2 before:left-[11px] before:w-px before:bg-line">
      <li className="relative flex gap-3 pl-0">
        <span aria-hidden className="relative z-[1] mt-0.5 grid size-6 flex-none place-items-center rounded-full border border-line bg-surface">
          <span className="size-2 rounded-full bg-sub" />
        </span>
        <p className="pt-0.5 text-sm text-sub">
          Обращение отправлено · <time dateTime={createdAt}>{when(createdAt)}</time>
        </p>
      </li>
      {events.map((e, i) => (
        <motion.li
          key={e.id}
          initial={reduce ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1], delay: Math.min(i, 5) * 0.05 }}
          className="relative flex gap-3"
        >
          <span
            aria-hidden
            className={`relative z-[1] mt-0.5 grid size-6 flex-none place-items-center rounded-full [&_svg]:size-3.5 ${
              e.body ? "bg-accent-grad text-white" : "border border-line bg-surface text-sub"
            }`}
          >
            {e.status === "done" ? <CheckIcon /> : e.status === "in_progress" ? <ClockIcon /> : <span className="size-2 rounded-full bg-current" />}
          </span>
          <div className="min-w-0 flex-1">
            <p className="pt-0.5 text-sm text-sub">
              {e.body ? <span className="font-semibold text-ink">{e.author ?? supportName}</span> : null}
              {e.body && e.status ? " · " : null}
              {e.status ? (
                <>
                  статус: <span className="font-semibold text-ink-2">{STATUS_LABEL[e.status].toLowerCase()}</span>
                </>
              ) : null}
              {" · "}
              <time dateTime={e.createdAt}>{when(e.createdAt)}</time>
            </p>
            {e.body && (
              <p className="mt-1.5 rounded-[14px] rounded-tl-[4px] border border-accent-line bg-accent-soft/60 px-3.5 py-2.5 text-[15px] leading-[22px] whitespace-pre-wrap break-words">
                {e.body}
              </p>
            )}
          </div>
        </motion.li>
      ))}
    </ol>
  );
}
